import { Link } from '@/lib/react-router-dom-shim';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { MapPin, ArrowRight } from 'lucide-react';

interface Props {
  iterpecDestinationId: string | null;
}

/**
 * Shown on hotel static page when a published destination guide exists
 * for this hotel's iterpec destination id.
 */
export const HotelStaticDestinationLink = ({ iterpecDestinationId }: Props) => {
  const { t } = useTranslation();

  const { data: destination } = useQuery({
    queryKey: ['destination-by-iterpec-id', iterpecDestinationId],
    enabled: !!iterpecDestinationId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('destinations')
        .select('slug, name, state')
        .eq('iterpec_destination_id', iterpecDestinationId)
        .eq('status', 'published')
        .maybeSingle();
      if (error) return null;
      return data;
    },
    staleTime: 10 * 60_000,
  });

  if (!destination) return null;

  return (
    <Link
      to={`/destino/${destination.slug}`}
      className="flex items-center justify-between gap-4 rounded-lg border bg-primary/5 p-4 transition-all hover:bg-primary/10 hover:shadow-sm"
    >
      <div className="flex items-center gap-3">
        <div className="rounded-full bg-primary/10 p-2">
          <MapPin className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">
            {t('hotelStatic.aboutDestination') || 'Sobre o destino'}
          </p>
          <p className="font-medium text-foreground">
            {destination.name}
            {destination.state ? `, ${destination.state}` : ''}
          </p>
        </div>
      </div>
      <ArrowRight className="h-5 w-5 text-primary" />
    </Link>
  );
};
